import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { ArrowLeft, BookOpen, Save, RotateCcw, Plus, Trash2, Sparkles, Heart } from 'lucide-react';
import { toast } from 'sonner';
import FeelingsNeedsHelper from '../FeelingsNeedsHelper';
import EbookPageFooter from '../shared/EbookPageFooter';

export default function OFNRPracticeSheets() {
  const navigate = useNavigate();
  const [isHelperOpen, setIsHelperOpen] = useState(false);
  
  const exampleScenarios = [
    {
      id: 'ex1',
      statement: 'Why do you spend so much money on beer when we\'re having trouble paying rent?',
      honestyExample: {
        observation: 'When I hear you say that ...',
        feeling: 'I feel frustrated and hurt',
        need: 'Because I need understanding and empathy',
        request: 'Would you be willing to schedule a time to have this conversation later.'
      },
      empathyExample: {
        observation: 'Hearing you say that ...',
        feeling: 'I wonder if you are feeling afraid',
        need: 'Because you need our family\'s well-being?',
        request: 'Would you like me to check with you before I spend money on alcohol in the future?'
      }
    },
    {
      id: 'ex2',
      statement: 'You should spend more time with me visiting my parents.',
      honestyExample: {
        observation: 'When I hear you say that ...',
        feeling: 'I feel disappointed and sad',
        need: 'Because I need autonomy and choice',
        request: 'Would you please tell me what you heard me say?'
      },
      empathyExample: {
        observation: 'Hearing you say that ...',
        feeling: 'Are you feeling lonely and overwhelmed?',
        need: 'Because you need connection and support?',
        request: 'Is this what\'s going on for you?'
      }
    },
    {
      id: 'ex3',
      statement: 'When I hear you tell the children to clean up their rooms',
      honestyExample: {
        observation: 'When I hear you tell the children to clean up their rooms',
        feeling: 'I feel concerned',
        need: 'Because I have a need for family harmony',
        request: 'Would you be willing for me to talk with the children about cleaning their rooms?'
      },
      empathyExample: {
        observation: 'When I hear you tell the children to clean up their rooms',
        feeling: 'Are you feeling annoyed and frustrated?',
        need: 'Because you really value a clean and healthy house?',
        request: 'Would you please tell me if that\'s what\'s going on for you?'
      }
    }
  ];

  const [customSheets, setCustomSheets] = useState(() => {
    const saved = localStorage.getItem('ofnrPracticeSheets');
    return saved ? JSON.parse(saved) : [{ id: Date.now(), statement: '', honesty: {}, empathy: {} }];
  });

  const [showExamples, setShowExamples] = useState(true);
  const [isGeneratingScenario, setIsGeneratingScenario] = useState(false);

  const generateScenario = async () => {
    setIsGeneratingScenario(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/generate-scenario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          context: 'NVC OFNR Practice - Generate a realistic jackal statement (critical/blaming language) that could be used for OFNR practice, similar to statements like "You never listen to me" or "Why do you always talk so much?"',
          page_number: 18
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Add new sheet with generated statement
        const newSheet = { 
          id: Date.now(), 
          statement: data.scenario, 
          honesty: {}, 
          empathy: {} 
        };
        setCustomSheets([newSheet, ...customSheets]);
        toast.success('New practice scenario generated!');
      } else {
        toast.error('Failed to generate scenario');
      }
    } catch (error) {
      toast.error('Error generating scenario');
    }
    setIsGeneratingScenario(false);
  };

  const saveProgress = () => {
    localStorage.setItem('ofnrPracticeSheets', JSON.stringify(customSheets));
    toast.success('Progress saved!');
  };

  const resetAll = () => {
    if (window.confirm('Reset all practice sheets?')) {
      setCustomSheets([{ id: Date.now(), statement: '', honesty: {}, empathy: {} }]);
      localStorage.removeItem('ofnrPracticeSheets');
      toast.success('Reset complete!');
    }
  };

  const addSheet = () => {
    setCustomSheets([...customSheets, { id: Date.now(), statement: '', honesty: {}, empathy: {} }]);
  };

  const deleteSheet = (id) => {
    if (customSheets.length > 1) {
      setCustomSheets(customSheets.filter(s => s.id !== id));
    }
  };

  const updateSheet = (id, field, subfield, value) => {
    setCustomSheets(prev => prev.map(sheet => 
      sheet.id === id 
        ? { ...sheet, [field]: subfield ? { ...sheet[field], [subfield]: value } : value }
        : sheet
    ));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      localStorage.setItem('ofnrPracticeSheets', JSON.stringify(customSheets));
    }, 30000);
    return () => clearInterval(interval);
  }, [customSheets]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">NVC OFNR Practice Sheets</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => setIsHelperOpen(true)} 
                variant="outline" 
                size="sm" 
                className="border-rose-600 hover:border-rose-500 text-white hover:bg-rose-900/20"
              >
                <Heart className="h-4 w-4 mr-2" />
                Feelings & Needs
              </Button>
              <Button onClick={() => setShowExamples(!showExamples)} variant="outline" size="sm" className="border-blue-600 hover:border-blue-500 text-white hover:bg-blue-900/20">
                {showExamples ? 'Hide' : 'Show'} Examples
              </Button>
              <Button 
                onClick={generateScenario} 
                disabled={isGeneratingScenario}
                variant="outline" 
                size="sm" 
                className="border-purple-600 hover:border-purple-500 text-white hover:bg-purple-900/20"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {isGeneratingScenario ? 'Generating...' : 'Generate Scenario'}
              </Button>
              <Button onClick={addSheet} variant="outline" size="sm" className="border-green-600 hover:border-green-500 text-white hover:bg-green-900/20">
                <Plus className="h-4 w-4 mr-2" />Add Sheet
              </Button>
              <Button onClick={resetAll} variant="outline" size="sm" className="border-red-600 hover:border-red-500 text-white hover:bg-red-900/20">
                <RotateCcw className="h-4 w-4 mr-2" />Reset All
              </Button>
              <Button onClick={saveProgress} variant="outline" size="sm" className="border-green-600 hover:border-green-500 text-white hover:bg-green-900/20">
                <Save className="h-4 w-4 mr-2" />Save
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Pages 18-24</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-slate-400 text-sm mb-6">💾 Auto-saves every 30 seconds</p>

          {/* Examples (Collapsible) */}
          {showExamples && (
            <div className="mb-8 space-y-6">
              <h2 className="text-2xl font-bold text-white mb-4">📚 Example Scenarios</h2>
              {exampleScenarios.map((example, idx) => (
                <div key={example.id} className="bg-blue-900/10 border border-blue-700/20 rounded-lg p-6">
                  <p className="text-blue-200 font-semibold mb-4">Example {idx + 1}: "{example.statement}"</p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <h4 className="text-purple-300 font-semibold">Honesty (Expressing):</h4>
                      <div className="text-sm space-y-2 text-slate-300">
                        <p><span className="text-blue-300">O:</span> {example.honestyExample.observation}</p>
                        <p><span className="text-red-300">F:</span> {example.honestyExample.feeling}</p>
                        <p><span className="text-green-300">N:</span> {example.honestyExample.need}</p>
                        <p><span className="text-amber-300">R:</span> {example.honestyExample.request}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <h4 className="text-cyan-300 font-semibold">Empathy (Listening):</h4>
                      <div className="text-sm space-y-2 text-slate-300">
                        <p><span className="text-blue-300">O:</span> {example.empathyExample.observation}</p>
                        <p><span className="text-red-300">F:</span> {example.empathyExample.feeling}</p>
                        <p><span className="text-green-300">N:</span> {example.empathyExample.need}</p>
                        <p><span className="text-amber-300">R:</span> {example.empathyExample.request}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Custom Practice Sheets */}
          <div className="space-y-8">
            <h2 className="text-2xl font-bold text-white mb-4">✍️ Your Practice Sheets</h2>
            {customSheets.map((sheet, sheetIdx) => (
              <div key={sheet.id} className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden">
                <div className="bg-slate-800/60 px-6 py-4 border-b border-slate-700/30 flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white">Practice Sheet #{sheetIdx + 1}</h3>
                  {customSheets.length > 1 && (
                    <Button onClick={() => deleteSheet(sheet.id)} variant="outline" size="sm" className="border-red-600 hover:bg-red-900/20 text-white">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="p-6 space-y-6">
                  {/* Statement Input */}
                  <div>
                    <label className="text-slate-300 font-semibold mb-2 block">Somebody said to me:</label>
                    <Input
                      value={sheet.statement}
                      onChange={(e) => updateSheet(sheet.id, 'statement', null, e.target.value)}
                      placeholder='e.g., "Why are you always late?"'
                      className="bg-slate-900/40 border-slate-700 text-white placeholder:text-slate-500"
                    />
                  </div>

                  {/* Honesty and Empathy Side by Side */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Honesty */}
                    <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-5">
                      <h4 className="text-lg font-bold text-purple-200 mb-4">Expressing Honesty</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-blue-300 text-sm mb-1 block">Observation: "When..."</label>
                          <Textarea value={sheet.honesty?.observation || ''} onChange={(e) => updateSheet(sheet.id, 'honesty', 'observation', e.target.value)} placeholder="When I hear/see..." className="bg-blue-900/20 border-blue-700/30 text-white placeholder:text-blue-300/40 min-h-[60px]" />
                        </div>
                        <div>
                          <label className="text-red-300 text-sm mb-1 block">Feeling: "I feel..."</label>
                          <Textarea value={sheet.honesty?.feeling || ''} onChange={(e) => updateSheet(sheet.id, 'honesty', 'feeling', e.target.value)} placeholder="I feel..." className="bg-red-900/20 border-red-700/30 text-white placeholder:text-red-300/40 min-h-[60px]" />
                        </div>
                        <div>
                          <label className="text-green-300 text-sm mb-1 block">Need: "Because I need..."</label>
                          <Textarea value={sheet.honesty?.need || ''} onChange={(e) => updateSheet(sheet.id, 'honesty', 'need', e.target.value)} placeholder="Because I need..." className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 min-h-[60px]" />
                        </div>
                        <div>
                          <label className="text-amber-300 text-sm mb-1 block">Request: "Would you be willing to..."</label>
                          <Textarea value={sheet.honesty?.request || ''} onChange={(e) => updateSheet(sheet.id, 'honesty', 'request', e.target.value)} placeholder="Would you be willing to..." className="bg-amber-900/20 border-amber-700/30 text-white placeholder:text-amber-300/40 min-h-[60px]" />
                        </div>
                      </div>
                    </div>

                    {/* Empathy */}
                    <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-5">
                      <h4 className="text-lg font-bold text-cyan-200 mb-4">Empathic Understanding</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-blue-300 text-sm mb-1 block">Observation: "When..."</label>
                          <Textarea value={sheet.empathy?.observation || ''} onChange={(e) => updateSheet(sheet.id, 'empathy', 'observation', e.target.value)} placeholder="When you hear/see..." className="bg-blue-900/20 border-blue-700/30 text-white placeholder:text-blue-300/40 min-h-[60px]" />
                        </div>
                        <div>
                          <label className="text-red-300 text-sm mb-1 block">Feeling: "Do you feel...?"</label>
                          <Textarea value={sheet.empathy?.feeling || ''} onChange={(e) => updateSheet(sheet.id, 'empathy', 'feeling', e.target.value)} placeholder="Do you feel...?" className="bg-red-900/20 border-red-700/30 text-white placeholder:text-red-300/40 min-h-[60px]" />
                        </div>
                        <div>
                          <label className="text-green-300 text-sm mb-1 block">Need: "Because you need..."</label>
                          <Textarea value={sheet.empathy?.need || ''} onChange={(e) => updateSheet(sheet.id, 'empathy', 'need', e.target.value)} placeholder="Because you need..." className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 min-h-[60px]" />
                        </div>
                        <div>
                          <label className="text-amber-300 text-sm mb-1 block">Request: "Would you like...?"</label>
                          <Textarea value={sheet.empathy?.request || ''} onChange={(e) => updateSheet(sheet.id, 'empathy', 'request', e.target.value)} placeholder="Would you like...?" className="bg-amber-900/20 border-amber-700/30 text-white placeholder:text-amber-300/40 min-h-[60px]" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Attribution */}
          <div className="text-center text-slate-400 text-sm mt-8">
            <p>Steve Torma • The REAL Center • www.theREALcenter.org • 828-254-5613</p>
          </div>

          {/* Practice Scenario & Contact Form */}
          <EbookPageFooter 
            pageContext="OFNR Practice Sheets"
            pageNumber={18}
            showScenario={true}
            showContactForm={true}
          />
        </div>
      </div>

      {/* Feelings & Needs Helper Popup */}
      <FeelingsNeedsHelper 
        isOpen={isHelperOpen} 
        onClose={() => setIsHelperOpen(false)} 
      />
    </div>
  );
}
